import React, { useEffect, useMemo, useState } from "react";

/**
 * AdminInitiativesOpsPage
 * - Internal-only operating checklist + governance notes for the 3 public initiatives tabs
 * - Stores checkbox state in localStorage (non-authoritative, but super practical)
 * - Designed to prevent overpromising: clear gates, statuses, and activation rules
 */

const LS_KEY = "ul_admin_initiatives_ops_v1";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Badge({ tone = "neutral", children }) {
  const cls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/25"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200 border-amber-500/25"
        : tone === "danger"
          ? "bg-red-500/15 text-red-200 border-red-500/25"
          : "bg-white/10 text-white/80 border-white/10";

  return (
    <span className={cx("inline-flex items-center rounded-full border px-3 py-1 text-xs", cls)}>
      {children}
    </span>
  );
}

function Card({ title, subtitle, right, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-white/70 max-w-3xl">{subtitle}</p>
          ) : null}
        </div>
        {right ? <div className="mt-2 md:mt-0">{right}</div> : null}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function ChecklistSection({ title, description, items, state, setState, prefix }) {
  const doneCount = items.reduce((acc, it) => acc + (state[`${prefix}.${it.id}`] ? 1 : 0), 0);
  const total = items.length;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4 md:p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm md:text-base font-semibold text-white">{title}</div>
          {description ? <div className="mt-1 text-sm text-white/70">{description}</div> : null}
        </div>
        <Badge tone={doneCount === total ? "ok" : doneCount > 0 ? "warn" : "neutral"}>
          {doneCount}/{total}
        </Badge>
      </div>

      <div className="mt-4 space-y-3">
        {items.map((it) => {
          const key = `${prefix}.${it.id}`;
          const checked = !!state[key];

          return (
            <label key={key} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-white"
                checked={checked}
                onChange={(e) => setState((s) => ({ ...s, [key]: e.target.checked }))}
              />
              <div>
                <div className="text-sm text-white">{it.label}</div>
                {it.hint ? <div className="text-xs text-white/60 mt-0.5">{it.hint}</div> : null}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function computeStatus(groups) {
  // groups: [{done, total}]
  const totals = groups.reduce(
    (acc, g) => ({ done: acc.done + g.done, total: acc.total + g.total }),
    { done: 0, total: 0 }
  );
  if (totals.total === 0) return { label: "Planned", tone: "neutral" };
  if (totals.done === 0) return { label: "Planned", tone: "neutral" };
  if (totals.done < totals.total) return { label: "Situational", tone: "warn" };
  return { label: "Active", tone: "ok" };
}

const TABS = [
  { id: "skills", label: "Future-Safe Skills" },
  { id: "parents", label: "Single Parent Support" },
  { id: "housing", label: "Community Housing" },
];

const CHECKLISTS = {
  skills: {
    title: "Future-Safe Skills & Opportunity Fund",
    subtitle:
      "Goal: keep this initiative honest and executable. Public page can exist anytime; award cycles only activate when governance + ops + partners are ready.",
    sections: [
      {
        id: "governance",
        title: "Governance readiness",
        description: "Define authority, cadence, and conflict-of-interest protection.",
        items: [
          { id: "scope", label: "Scope locked (what we fund / do not fund)" },
          { id: "cadence", label: "Award cadence defined (annual/biannual)", hint: "No dates on public page unless you can keep them." },
          { id: "authority", label: "Decision authority defined (advisory vs final)" },
          { id: "coi", label: "Conflict-of-interest rules documented" },
        ],
      },
      {
        id: "ops",
        title: "Operational readiness",
        description: "Make it run without heroics.",
        items: [
          { id: "intake", label: "Application/nomination intake ready", hint: "Even if closed, it should be ready." },
          { id: "eligibility", label: "Eligibility criteria documented (internal)" },
          { id: "voucher", label: "Voucher-based delivery confirmed (no cash)" },
          { id: "tracking", label: "Outcome tracking defined (completion / placement / next-step)" },
        ],
      },
      {
        id: "partners",
        title: "Partner readiness",
        description: "School/program partners + voucher redemption flow validated.",
        items: [
          { id: "identify", label: "Partner list started (internal)" },
          { id: "verify", label: "Accreditation/quality checks defined" },
          { id: "redeem", label: "Voucher redemption process confirmed" },
          { id: "privacy", label: "Data handling expectations defined (minimum necessary)" },
        ],
      },
      {
        id: "risk",
        title: "Risk & public copy audit",
        description: "Make sure public messaging never implies guarantees.",
        items: [
          { id: "copy", label: "Public language reviewed (no promises, no timelines)" },
          { id: "capacity", label: "Capacity limits documented (internal)" },
          { id: "appeals", label: "Dispute/appeal path defined (internal)" },
        ],
      },
    ],
    instructions: [
      "Keep the public page stable and timeless; make the execution cycle flexible.",
      "If partners are not locked, awards remain situational—even if the fund has money.",
      "Prefer multiple smaller vouchers over one giant award early on (more impact, less risk).",
    ],
  },

  parents: {
    title: "Single Parent Support & Family Stability",
    subtitle:
      "Goal: reduce stress with essentials while avoiding dependency and overcommitment. Transportation + childcare remain situational/'coming soon' until fully operational.",
    sections: [
      {
        id: "governance",
        title: "Governance readiness",
        description: "Define what is always-on vs situational and how to prevent abuse.",
        items: [
          { id: "categories", label: "Support categories approved (supplies/food/others)" },
          { id: "always", label: "Always-on supports defined (baseline availability)" },
          { id: "situational", label: "Situational supports defined (capacity-gated)", hint: "Transportation + childcare belong here for now." },
          { id: "controls", label: "Basic abuse-prevention controls defined" },
        ],
      },
      {
        id: "ops",
        title: "Operational readiness",
        description: "Deliver essentials cleanly and consistently.",
        items: [
          { id: "intake", label: "Application/nomination intake ready" },
          { id: "verify", label: "Verification process defined (lightweight, respectful)" },
          { id: "delivery", label: "Voucher/credit delivery tested end-to-end" },
          { id: "calendar", label: "Seasonal calendar drafted (back-to-school, holidays)" },
        ],
      },
      {
        id: "partners",
        title: "Partner readiness",
        description: "Supplies/groceries are best handled with partners.",
        items: [
          { id: "supplies", label: "School supply partner(s) identified" },
          { id: "grocery", label: "Grocery/household partner(s) identified" },
          { id: "fulfill", label: "Fulfillment logistics confirmed (how items reach families)" },
        ],
      },
      {
        id: "risk",
        title: "Risk & public copy audit",
        description: "Prevent promises. Keep situational supports clearly labeled.",
        items: [
          { id: "copy", label: "Public language reviewed (no guarantees)" },
          { id: "comingsoon", label: "Transportation/childcare labeled 'situational' or 'coming soon'" },
          { id: "limits", label: "Internal limits documented (per cycle/per household)" },
        ],
      },
    ],
    instructions: [
      "Always-on should be essentials you can reliably deliver with partners.",
      "Transportation + childcare stay situational until you have partners + workflow + capacity limits.",
      "Keep the process dignified: no public stories, no spotlight, no shame mechanics.",
    ],
  },

  housing: {
    title: "Community Housing & Stability",
    subtitle:
      "Goal: grant-ready structure without exposing sensitive details publicly. Build the machine first; build buildings second.",
    sections: [
      {
        id: "governance",
        title: "Governance readiness",
        description: "Define model, authority, and safety expectations before anything launches.",
        items: [
          { id: "population", label: "Population focus documented (women / vets)" },
          { id: "model", label: "Transitional model defined (not permanent shelter)" },
          { id: "authority", label: "Resident selection authority assigned (internal)" },
          { id: "safety", label: "Safety & security baseline defined (internal)" },
        ],
      },
      {
        id: "ops",
        title: "Operational readiness",
        description: "Site selection criteria, compliance approach, partner operator plan.",
        items: [
          { id: "criteria", label: "Pilot city criteria defined" },
          { id: "compliance", label: "Zoning/compliance research started" },
          { id: "operator", label: "Operator model selected (partner-run vs hybrid)" },
          { id: "phases", label: "Phased execution plan drafted (pilot → expand)" },
        ],
      },
      {
        id: "partners",
        title: "Partner readiness",
        description: "This must be partnership-first.",
        items: [
          { id: "operators", label: "Housing operators identified" },
          { id: "services", label: "Support service partners identified (case mgmt, workforce, etc.)" },
          { id: "municipal", label: "Municipal alignment path identified (where applicable)" },
        ],
      },
      {
        id: "risk",
        title: "Risk & public copy audit",
        description: "Public page must remain high-level and non-specific.",
        items: [
          { id: "copy", label: "Public language reviewed (no locations, counts, dates)" },
          { id: "grantlanes", label: "Grant lanes mapped internally (housing/veterans)" },
          { id: "capitalstack", label: "Capital stack model drafted internally (how it gets funded)" },
        ],
      },
    ],
    instructions: [
      "Keep public copy aspirational but non-specific: no locations, unit counts, or dates.",
      "Treat this as a program that becomes real in phases; pilots first, expansion later.",
      "Partnerships are the accelerator—operators and municipalities reduce risk massively.",
    ],
  },
};

export default function AdminInitiativesOpsPage() {
  const [active, setActive] = useState("skills");
  
  // Load from localStorage on mount
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      // ignore
    }
    return {};
  });

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  const tabData = CHECKLISTS[active];

  const progress = useMemo(() => {
    const sections = tabData.sections.map((s) => {
      const done = s.items.reduce((acc, it) => acc + (state[`${active}.${s.id}.${it.id}`] ? 1 : 0), 0);
      return { done, total: s.items.length };
    });
    return computeStatus(sections);
  }, [active, state, tabData.sections]);

  const totalProgress = useMemo(() => {
    const keys = Object.keys(CHECKLISTS);
    const all = keys.flatMap((k) =>
      CHECKLISTS[k].sections.flatMap((s) => s.items.map((it) => `${k}.${s.id}.${it.id}`))
    );
    const done = all.reduce((acc, key) => acc + (state[key] ? 1 : 0), 0);
    return { done, total: all.length };
  }, [state]);

  const clearAll = () => {
    setState({});
  };

  const markTabPlanned = () => {
    // clears only current tab keys
    const next = { ...state };
    Object.keys(next).forEach((k) => {
      if (k.startsWith(`${active}.`)) delete next[k];
    });
    setState(next);
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-4xl font-bold text-white">Initiatives Ops</h1>
          <p className="text-sm md:text-base text-white/70 max-w-3xl">
            Internal operating checklist for Unity Love&apos;s public initiatives. This page is designed to prevent
            overpromising by gating &quot;Active&quot; status behind governance, ops, partners, and copy audits.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge tone="neutral">Overall: {totalProgress.done}/{totalProgress.total}</Badge>
            <Badge tone={progress.tone}>{tabData.title}: {progress.label}</Badge>

            <button
              onClick={markTabPlanned}
              className="ml-0 md:ml-2 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
              title="Clear checkboxes for the currently selected initiative"
            >
              Reset this initiative
            </button>

            <button
              onClick={clearAll}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
              title="Clear all initiatives checkboxes"
            >
              Reset all
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <div className="flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-white/5 p-2">
            {TABS.map((t) => {
              const isActive = t.id === active;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={cx(
                    "px-4 py-2 rounded-xl text-sm transition-colors",
                    isActive ? "bg-white text-black" : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            <Card
              title={tabData.title}
              subtitle={tabData.subtitle}
              right={<Badge tone={progress.tone}>{progress.label}</Badge>}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {tabData.sections.map((s) => (
                  <ChecklistSection
                    key={s.id}
                    title={s.title}
                    description={s.description}
                    items={s.items}
                    state={state}
                    setState={setState}
                    prefix={`${active}.${s.id}`}
                  />
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-4 md:p-5">
                <div className="text-sm md:text-base font-semibold text-white">Operator instructions</div>
                <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-white/80">
                  {tabData.instructions.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>

                <div className="mt-4 text-xs text-white/60">
                  Activation rule: If any section is incomplete, treat this initiative as <b>Situational</b>. Only
                  mark it <b>Active</b> once governance + ops + partners + copy audit are fully complete.
                </div>
              </div>
            </Card>

            <Card
              title="Linking note"
              subtitle="This page is internal. The public Initiatives page should remain timeless and high-level."
            >
              <div className="text-sm text-white/80">
                Public copy should avoid dates, guarantees, unit counts, locations, partner names (early), and any
                wording that implies entitlement. Keep execution flexible and capacity-gated.
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
